import {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import Plyr from 'plyr-react';
import 'plyr-react/plyr.css';
import S from './SingleVideoStyles';
import NoVideoContent from '../MultipleVideo/NoVideoContent/NoVideoContent';
import useGetRole from '../../../API/hooks/useGetRole';

function SingleVideo({
  url,
  width = '100%',
  height = '100%',
  isFullSize = true,
  fillSlot = false,
  videoId,
  onTimeChange,
  selectedTime,
  isPlaying = false,
  hasAudio = true,
  status,
  onDurationChange,
}: any): JSX.Element {
  const { canPlayVideoAudio, canDownloadVideo } = useGetRole();
  // Fleet managers and fleet viewers never get audio, regardless of the video track.
  const audioEnabled = hasAudio && canPlayVideoAudio;
  const downloadEnabled = canDownloadVideo;
  const playerRef = useRef<any>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoMetadataLoaded, setVideoMetadataLoaded] = useState(false);
  const [previewInfo, setPreviewInfo] = useState<{ time: number; left: number } | null>(null);
  const lastProcessedTime = useRef<number>(0);
  const [timelineIsActive, setTimelineIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsPlayerReady(false);
    setVideoMetadataLoaded(false);
    return undefined;
  }, []);
  useEffect(() => {
    if (url) {
      const videoEl = document.createElement('video');
      videoEl.src = url;

      const handleMetadata = (): void => {
        if (videoEl.duration && videoEl.duration !== Infinity) {
          const roundedDuration = Math.round(videoEl.duration);
          setVideoDuration(roundedDuration);
          setVideoMetadataLoaded(true);
          onDurationChange?.(roundedDuration);
        }
        return undefined;
      };

      videoEl.addEventListener('loadedmetadata', handleMetadata);

      return () => {
        videoEl.removeEventListener('loadedmetadata', handleMetadata);
        return undefined;
      };
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (videoMetadataLoaded) {
      setIsPlayerReady(true);
    }
  }, [videoMetadataLoaded]);

  const generatePreviewFrame = useCallback((time: number) => {
    if (!previewVideoRef.current || !previewCanvasRef.current) return;

    const video = previewVideoRef.current;
    const canvas = previewCanvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    video.currentTime = time;
    video.addEventListener('seeked', () => {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }, { once: true });
  }, [url]);

  const handleTimelineHover = useCallback((e: MouseEvent) => {
    const now = Date.now();
    if (now - lastProcessedTime.current < 150) return;
    lastProcessedTime.current = now;

    const progressBar = (e.target as HTMLElement).closest('.plyr__progress__container');
    if (!progressBar || !videoDuration) return;

    const rect = progressBar.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = position * videoDuration;

    const newPosition = {
      time: Math.max(0, Math.min(videoDuration, time)),
      left: e.clientX - rect.left,
    };

    setPreviewInfo(newPosition);
    generatePreviewFrame(time);
  }, [videoDuration, generatePreviewFrame]);

  const handleTimelineLeave = useCallback(() => {
    setPreviewInfo(null);
  }, []);

  const handlePlayerPlay = useCallback((playing: boolean): void => {
    onTimeChange({
      time: playerRef.current?.plyr.currentTime,
      videoId,
      playing,
    });
  }, [onTimeChange, videoId]);

  const handleTimelineMouseDown = useCallback(() => {
    setTimelineIsActive(true);
    onTimeChange({
      time: playerRef.current?.plyr.currentTime,
      videoId,
    });
  }, [timelineIsActive]);

  const handleTimelineMouseUp = useCallback(() => {
    setTimelineIsActive(false);
    onTimeChange({
      time: playerRef.current?.plyr.currentTime,
      videoId,
    });
  }, [timelineIsActive]);

  const handleTimelineMouseMove = useCallback(() => {
    if (timelineIsActive) {
      onTimeChange({
        time: playerRef.current?.plyr.currentTime,
        videoId,
        playing: playerRef.current?.plyr.playing,
      });
    }
  }, [timelineIsActive]);

  useEffect(() => {
    if (selectedTime !== undefined && playerRef.current) {
      const maxDuration = playerRef?.current?.plyr.duration;
      playerRef.current.plyr.currentTime = selectedTime <= maxDuration ? selectedTime : maxDuration;
    }
  }, [selectedTime]);

  useEffect(() => {
    const plyr = playerRef.current?.plyr;
    if (!plyr) return;

    if (isPlaying && typeof plyr.play === 'function' && !plyr.playing) {
      plyr.play();
    } else if (!isPlaying && typeof plyr.pause === 'function' && plyr.playing) {
      plyr.pause();
    }
  }, [isPlaying]);

  const playerContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFullSize || !isPlayerReady || !playerContainerRef.current) return undefined;
    const videoEl = playerContainerRef.current.querySelector('video');
    if (!videoEl) return undefined;
    const onPlay = (): void => handlePlayerPlay(true);
    const onPause = (): void => handlePlayerPlay(false);
    const onEnded = (): void => {
      if (playerRef.current?.plyr) {
        playerRef.current.plyr.currentTime = 0;
      }
      onTimeChange({ time: 0, videoId, playing: false });
    };
    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    videoEl.addEventListener('ended', onEnded);
    return () => {
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
      videoEl.removeEventListener('ended', onEnded);
    };
  }, [isFullSize, isPlayerReady, handlePlayerPlay, onTimeChange, videoId]);

  useEffect(() => {
    if (!isPlayerReady || !playerContainerRef.current || !isFullSize) return undefined;
    const currentContainer = playerContainerRef.current;

    const progressContainer = currentContainer?.querySelector('.plyr__progress__container') as HTMLDivElement;
    if (!progressContainer) return undefined;

    progressContainer.addEventListener('mousemove', handleTimelineHover);
    progressContainer.addEventListener('mouseleave', handleTimelineLeave);

    return () => {
      if (progressContainer) {
        progressContainer.removeEventListener('mousemove', handleTimelineHover);
        progressContainer.removeEventListener('mouseleave', handleTimelineLeave);
      }
    };
  }, [handleTimelineHover, handleTimelineLeave, isPlayerReady, isFullSize]);

  useEffect(() => {
    if (!isPlayerReady || !playerContainerRef.current) return undefined;
    const currentContainer = playerContainerRef.current;

    const progressContainer = currentContainer?.querySelector('.plyr__progress__container') as HTMLDivElement;
    if (!progressContainer) return undefined;

    if (!isFullSize) {
      progressContainer.addEventListener('mousedown', handleTimelineMouseDown);
      progressContainer.addEventListener('mouseup', handleTimelineMouseUp);
      progressContainer.addEventListener('mousemove', handleTimelineMouseMove);
    }

    return () => {
      if (progressContainer && !isFullSize) {
        progressContainer.removeEventListener('mousedown', handleTimelineMouseDown);
        progressContainer.removeEventListener('mouseup', handleTimelineMouseUp);
        progressContainer.removeEventListener('mousemove', handleTimelineMouseMove);
      }
    };
  }, [handleTimelineMouseDown, handleTimelineMouseUp, handleTimelineMouseMove, isPlayerReady]);

  const playerProps = useMemo(() => ({
    source: {
      type: 'video' as const,
      sources: [{ src: url }],
    },
    options: {
      controls: [
        'play-large',
        'play',
        'progress',
        'current-time',
        ...(audioEnabled ? ['mute'] : []),
        ...(audioEnabled && isFullSize ? ['volume'] : []),
        ...(isFullSize ? ['pip'] : []),
        'fullscreen',
        ...(downloadEnabled ? ['download'] : []),
      ],
      download: downloadEnabled,
      ...(downloadEnabled ? { urls: { download: url } } : {}),
      playsinline: true,
      // Right-click "Save video as..." is a download path too, so the native
      // menu stays available only for roles allowed to download.
      disableContextMenu: !downloadEnabled,
      muted: !audioEnabled,
      ...(videoDuration ? { duration: videoDuration } : {}),
    },
  }), [url, videoDuration, isFullSize, audioEnabled, downloadEnabled]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <S.Wrapper isFullSize={isFullSize} fillSlot={fillSlot}>
      <div className="video-player" ref={playerContainerRef}>
        {url && (
          <>
            <div style={{ visibility: isPlayerReady ? 'visible' : 'hidden', width, height }}>
              <Plyr
                id="plyr"
                ref={playerRef}
                {...playerProps}
              />
              {previewInfo && isFullSize && (
                <div
                  className="preview-tooltip"
                  style={{ left: previewInfo.left }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    className="preview-frame"
                    width="160"
                    height="90"
                  />
                  <span className="preview-time">{formatTime(previewInfo.time)}</span>
                </div>
              )}
            </div>
            {isFullSize && (
              /* eslint-disable-next-line jsx-a11y/media-has-caption */
              <video
                ref={previewVideoRef}
                src={url}
                className="hidden-video"
                preload="auto"
                muted={!audioEnabled}
              // onChange={onChange}
              />
            )}
          </>
        )}
        {!url && (
          <NoVideoContent />
        )}
      </div>
    </S.Wrapper>
  );
}

export default SingleVideo;
